import debounce from "lodash/debounce";
import { useState } from "react";
import { FormattedMessage, useIntl } from "react-intl";
import { Text } from "@/components/atoms/Text";
import SearchCommand from "@/components/molecules/Command/SearchCommand";
import { cn } from "@/lib/utils";
interface FormValues {
  activity_selection: string;
  location: string;
  mobile_activity_selection: { id: string; text: string };
  mobile_location_selection: { id: string; text: string };
}

interface MatchedSubstring {
  length: number;
  offset: number;
}

interface PlacePrediction {
  description: string;
  matched_substrings: MatchedSubstring[];
  place_id: string;
  reference: string;
  structured_formatting: StructuredFormatting;
  terms: Term[];
  types: string[];
}

interface StructuredFormatting {
  main_text: string;
  main_text_matched_substrings: MatchedSubstring[];
  secondary_text: string;
}

interface Term {
  offset: number;
  value: string;
}

const SearchLocationDropdown = ({
  setIsValidLocation,
  showLabel = true,
  commandListClasses,
  commandInputClasses,
  formName,
  commandItemClasses,
  onSelectCallback,
  onLocationSearch,
  GOOGLE_MAPS_BROWSER_KEY,
  shouldCloseOnBlur,
}: {
  setIsValidLocation: (isValid: { id: string; text: string }) => void;
  showLabel?: boolean;
  commandListClasses?: string;
  commandInputClasses?: string;
  formName?: string;
  commandItemClasses?: string;
  onSelectCallback?: (value: { id: string; text: string }) => void;
  onLocationSearch: (
    value: string,
    GOOGLE_MAPS_BROWSER_KEY: string,
    setPredictions: (predictions: PlacePrediction[]) => void,
    setNoResults: (noResults: boolean) => void,
  ) => Promise<void>;
  GOOGLE_MAPS_BROWSER_KEY: string;
  shouldCloseOnBlur?: boolean;
}) => {
  const intl = useIntl();
  const [predictions, setPredictions] = useState<PlacePrediction[]>([]);
  const [noResults, setNoResults] = useState(false);
  const [isFetchingData, setIsFetchingData] = useState(false);
  const locationLabel = intl.formatMessage({
    id: "listing.location_dropdown.where_message",
    defaultMessage: "Where?",
  });
  const noLocationFoundText = intl.formatMessage({
    id: "search.location_error_dropdown.dont_recognize",
    defaultMessage: "We don't recognize that location",
  });

  const locationPlaceholder = intl.formatMessage({
    id: "elements_search_module.with_location_dropdown_hoc.enter_city",
    defaultMessage: "Enter a city, zip, or address",
  });

  const onLocationChange = debounce(async (value: string) => {
    if (!value) {
      setPredictions([]);
      return;
    }

    setIsValidLocation({ id: "", text: "" });
    setIsFetchingData(true);

    try {
      await onLocationSearch(
        value,
        GOOGLE_MAPS_BROWSER_KEY,
        setPredictions,
        setNoResults,
      );
    } catch {
      setPredictions([]);
      setNoResults(true);
    } finally {
      setIsFetchingData(false);
    }
  }, 400);

  return (
    <SearchCommand<FormValues>
      setIsValid={setIsValidLocation}
      valueList={predictions.map((prediction) => ({
        id: prediction.place_id,
        text: prediction.description,
      }))}
      formFields={{
        formName: (formName || "location") as keyof FormValues,
        placeholder: locationPlaceholder,
        label: locationLabel,
      }}
      filterAction={onLocationChange}
      emptyMessage={noLocationFoundText}
      noResults={noResults}
      isFetchingData={isFetchingData}
      onSelectCallback={onSelectCallback}
      shouldCloseOnBlur={shouldCloseOnBlur}
      additionalMessage={
        <div className={cn("tw-py-8 lg:tw-px-80", commandItemClasses)}>
          <Text typeface="caption-2" className="tw-text-gray-600">
            <FormattedMessage
              id="shared_components.location-input.google"
              defaultMessage="Results powered by Google"
            />
          </Text>
        </div>
      }
      showLabel={showLabel}
      commandListClasses={commandListClasses}
      commandInputClasses={commandInputClasses}
      commandItemClasses={commandItemClasses}
    />
  );
};

export default SearchLocationDropdown;
